
import React, { useState } from 'react';
import './BookingPage.css';

function BookingPage() {

  const [availableHours, setAvailableHours] = useState([]);
  const [isHoliday, setIsHoliday] = useState(false);
  const [availableHoursLoading, setAvailableHoursLoading] = useState(false);
  const [availableHoursError, setAvailableHoursError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    carNumber: '',
    makeModel: '',
    carType: '',
    tyreSize: '',
    numTyres: '',
    serviceType: '',
    wheelsOnRims: false,
    wheelsNeedBalancing: false,
    rustySills: false,
    pumpNitrogen: false,
    agreedToTerms: false,
  });
  const [touched, setTouched] = useState({});
  const today = new Date();

  // Fetch available hours for a date
  const fetchAvailableHours = async (dateStr) => {
    setAvailableHoursLoading(true);
    setAvailableHoursError(null);
    try {
      const res = await fetch(`/api/available_hours?date=${dateStr}`);
      if (!res.ok) throw new Error('Грешка при зареждане на свободните часове.');
      const data = await res.json();
      setAvailableHours(data.availableSlots);
      setIsHoliday(Boolean(data.isHoliday));
      

    } catch (err) {
      setAvailableHours([]);
      setIsHoliday(false);
      setAvailableHoursError(err.message || 'Грешка при зареждане на свободните часове.');
    } finally {
      setAvailableHoursLoading(false);
    }
  };

  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Map frontend carType to schema enum: 1 - Car, 2 - Jeep, 3 - Bus
  const carTypeEnum = { carChoice: 1, jeepChoice: 2, busChoice: 3 };
  // Map frontend serviceType to schema enum: 1 - Change, 2 - Repair
  const serviceTypeEnum = { changeTyres: 1, repairTyres: 2 };

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    // Compose appointment object for backend
    try {
      // Fetch user's IP address before creating appointment object
      let ipAddress = '';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        if (ipRes.ok) {
          const ipData = await ipRes.json();
          ipAddress = ipData.ip || '192.168.210.100';
        }
      } catch (e) {
        ipAddress = '192.168.210.100';
      }

      const appointment = {
        appointmentDate: selectedDate ? formatDate(selectedDate) : null,
        appointmentTime: selectedHour,
        customerName: form.name,
        phoneNumber: form.phone,
        email: form.email,
        makeAndModel: form.makeModel,
        carNumber: form.carNumber,
        carType: carTypeEnum[form.carType] || -1,
        tireSize: Number(form.tyreSize),
        serviceType: serviceTypeEnum[form.serviceType] || -1,
        numberOfWheels: Number(form.numTyres),
        wheelsOnRims: !!form.wheelsOnRims,
        wheelsNeedBalancing: !!form.wheelsNeedBalancing,
        rustySills: !!form.rustySills,
        pumpNitrogen: !!form.pumpNitrogen,
        ipAddress
      };

      const response = await fetch(`/api/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointment),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Грешка при записване на резервацията.');
      }
      // Show confirmation modal until user closes it (closeModal will reset form/state)
      setConfirmed(true);
    } catch (err) {
      setError(err?.message || 'Грешка при записване на резервацията.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(t => ({ ...t, [name]: true }));
  };
  const isValid =
    form.phone &&
    form.carNumber &&
    form.makeModel &&
    form.carType &&
    form.serviceType &&
    form.tyreSize &&
    form.numTyres &&
    Number(form.numTyres) >= 1 &&
    Number(form.numTyres) <= 6;
    

  const isPast = (date) => {
    if (!date) return true;
    return date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const formatDate = (date) =>
    date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      : '';

  // Bulgarian display formatter: 11.10.2025г. (събота)
  const formatDateDisplay = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}г.`;
  };

  function getMonthDays(year, month) {
    const days = [];
    const lastDay = new Date(year, month + 1, 0);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    return days;
  }

  const [month, setMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const days = getMonthDays(month.year, month.month);
  let firstDayOfWeek = new Date(month.year, month.month, 1).getDay();
  if (firstDayOfWeek === 0) firstDayOfWeek = 7; // Sunday -> 7
  const weeks = [];
  let week = Array(firstDayOfWeek - 1).fill(null);
  days.forEach((date) => {
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
    week.push(date);
  });
  while (week.length < 7) week.push(null);
  weeks.push(week);

  // When a date is selected, fetch available hours
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedHour('');
    if (date) {
      fetchAvailableHours(formatDate(date));
    } else {
      setAvailableHours([]);
      setIsHoliday(false);
    }
  };

  // Deselect selectedHour if it becomes unavailable
  // Helper to check availability whether API returns strings or objects
  const isHourAvailable = React.useCallback((time) => {
    if (!time) return false;
    return availableHours.some(h => (typeof h === 'string' ? h === time : (h && h.time === time)));
  }, [availableHours]);

  React.useEffect(() => {
    if (selectedHour && !isHourAvailable(selectedHour)) {
      setSelectedHour('');
    }
  }, [availableHours, selectedHour, isHourAvailable]);

  // Close modal and reset booking form/background data
  const closeModal = () => {
    setConfirmed(false);
    setSelectedDate(null);
    setSelectedHour('');
    setAvailableHours([]);
    setIsHoliday(false);
    setAvailableHoursError(null);
    setError(null);
    setSaving(false);
    setForm({
      name: '',
      phone: '',
      email: '',
      carNumber: '',
      makeModel: '',
      carType: '',
      tyreSize: '',
      numTyres: '',
      serviceType: '',
      wheelsOnRims: false,
      wheelsNeedBalancing: false,
      rustySills: false,
      pumpNitrogen: false,
      agreedToTerms: false,
    });
    setTouched({});
  };

  return (
    <div className="booking-modern">
      <div className="booking-card">
        <h2 className="booking-title">Запазете час</h2>
        <form className="booking-fields-form" onSubmit={e => { e.preventDefault(); }}>
        <p className="booking-required-note" >
          Полетата със знак * са задължителни.
        </p>
          <div className="booking-fields single-column">
            <div className="booking-field">
              <label>Имена/Име на фирма</label>
              <input name="name" value={form.name} onChange={handleInputChange} onBlur={handleBlur} className="styled-input" />
            </div>
            <div className="booking-field">
              <label>Тел. номер*</label>
              <input name="phone" value={form.phone} onChange={handleInputChange} onBlur={handleBlur} required className="styled-input" />
              {touched.phone && !form.phone && <span className="field-error">Задължително поле</span>}
            </div>
            {/* <div className="booking-field">
              <label>Имейл</label>
              <input name="email" type="email" value={form.email} onChange={handleInputChange} onBlur={handleBlur} className="styled-input" />
            </div> */}
            <div className="booking-field">
              <label>Номер на колата*</label>
              <input name="carNumber" value={form.carNumber} onChange={handleInputChange} onBlur={handleBlur} required className="styled-input" />
              {touched.carNumber && !form.carNumber && <span className="field-error">Задължително поле</span>}
              <div className="checkbox-row">
              </div>
            </div>
            <div className="booking-field">
              <label>Марка и Модел*</label>
              <input name="makeModel" value={form.makeModel} onChange={handleInputChange} onBlur={handleBlur} required className="styled-input" />
              {touched.makeModel && !form.makeModel && <span className="field-error">Задължително поле</span>}
            </div>
            <div className="booking-field">
              <label>Тип автомобил*</label>
              <div className="carType">
                <label><input type="radio" name="carType" value="carChoice" checked={form.carType === 'carChoice'} onChange={handleInputChange} required /> Лек автомобил/SUV </label>
                <label><input type="radio" name="carType" value="jeepChoice" checked={form.carType === 'jeepChoice'} onChange={handleInputChange} required /> Джип </label>
                <label><input type="radio" name="carType" value="busChoice" checked={form.carType === 'busChoice'} onChange={handleInputChange} required /> Бус </label>
              </div>
              {touched.carType && !form.carType && <span className="field-error">Задължително поле</span>}
            </div>
            <div className="booking-field">
              <label>Размер на гумите*</label>
              <select name="tyreSize" value={form.tyreSize} onChange={handleInputChange} onBlur={handleBlur} required className="styled-input">
                <option value="">Изберете размер...</option>
                {(form.carType === 'busChoice'
                  ? Array.from({ length: 5 }, (_, i) => 12 + i) // 12-16
                  : Array.from({ length: 11 }, (_, i) => 12 + i) // 12-22
                ).map(size => (
                  <option key={size} value={size}>{size}"</option>
                ))}
              </select>
              {touched.tyreSize && !form.tyreSize && <span className="field-error">Задължително поле</span>}
            </div>
            <div className="booking-field">
              <label>Услуга*</label>
              <select name="serviceType" value={form.serviceType} onChange={handleInputChange} onBlur={handleBlur} required className="styled-input">
                <option value="">Изберете услуга...</option>
                <option value="changeTyres">Смяна на гуми</option>
                <option value="repairTyres">Ремонт на гуми</option>
              </select>
              {touched.serviceType && !form.serviceType && <span className="field-error">Задължително поле</span>}
            </div>
            <div className="booking-field">
              <label>Брой гуми*</label>
              <input name="numTyres" type="number" min="1" max="6" value={form.numTyres} onChange={handleInputChange} onBlur={handleBlur} required className="styled-input" />
              {touched.numTyres && (!form.numTyres || Number(form.numTyres) < 1 || Number(form.numTyres) > 6) && <span className="field-error">Валидни са между 1 и 6</span>}
            </div>
            {form.serviceType === 'changeTyres' && (
              <>
                <div className="booking-field">
                  <label><input type="checkbox" name="wheelsOnRims" checked={form.wheelsOnRims} onChange={handleInputChange} /> Гумите са на джанти</label>
                </div>
                <div className="booking-field">
                  <label><input type="checkbox" name="wheelsNeedBalancing" checked={form.wheelsNeedBalancing} onChange={handleInputChange} disabled={!form.wheelsOnRims} /> Гумите ще се балансират </label>
                </div>
              </>
            )}
            <div className="booking-field">
              <label><input type="checkbox" name="rustySills" checked={form.rustySills} onChange={handleInputChange} /> Колата има изгнили прагове</label>
            </div>

            <div className="booking-field">
              <label><input type="checkbox" name="pumpNitrogen" checked={form.pumpNitrogen} onChange={handleInputChange} /> Помпене с азот</label>
            </div>
            <div className="booking-field calendar-widget">
              <div className="calendar-header">
                <button
                  className="calendar-nav"
                  onClick={() => setMonth(m => ({
                    year: m.month === 0 ? m.year - 1 : m.year,
                    month: m.month === 0 ? 11 : m.month - 1
                  }))}
                  aria-label="Previous Month"
                >
                  ◀
                </button>
                <span className="calendar-month-label">
                  {new Date(month.year, month.month).toLocaleString('bg-BG', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  className="calendar-nav"
                  onClick={() => setMonth(m => ({
                    year: m.month === 11 ? m.year + 1 : m.year,
                    month: m.month === 11 ? 0 : m.month + 1
                  }))}
                  aria-label="Next Month"
                >
                  ▶
                </button>
              </div>
              <div className="calendar-grid">
                {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"].map(d => (
                  <div key={d} className="calendar-day-label">{d}</div>
                ))}
                {weeks.flat().map((date, idx) => {
                  const isSelected = selectedDate && date && formatDate(date) === formatDate(selectedDate);
                  return (
                    <button
                      key={idx}
                      className={`calendar-cell${isSelected ? ' selected' : ''}`}
                      disabled={isPast(date)}
                      onClick={() => {
                        if (date && !isPast(date)) {
                          handleDateSelect(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
                        }
                      }}
                    >
                      {date ? date.getDate() : ''}
                    </button>
                  );
                })}
              </div>
            </div>
            {selectedDate && !confirmed && (
              <div className="hours-section">
                <h3 className="hours-title">Свободни часове за {formatDateDisplay(selectedDate)}</h3>
                {availableHoursLoading ? (
                  <div>Зареждане на свободните часове...</div>
                ) : availableHoursError ? (
                  <div className="field-error">{availableHoursError}</div>
                ) : availableHours.length <= 0 ? <div>Няма налични часове{isHoliday ? ' - избраният ден е почивен' : ''}</div> : (
                  <div className="hours-list-modern">
                    {availableHours.map((hour, idx) => {
                      const time = typeof hour === 'string' ? hour : hour.time;
                      return (
                        <button
                          key={time || idx}
                          className={`hour-btn-modern${selectedHour === time ? ' selected' : ''}`}
                          onClick={() => setSelectedHour(time)}
                          type="button"
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>
        </form>

        {error && (
          <div className="field-error" style={{ marginTop: '1rem', textAlign: 'center' }}>{error}</div>
        )}
        {selectedHour && !confirmed && (
          <form className="confirmation-modern" onSubmit={e => { e.preventDefault(); setTouched(t => ({ ...t, agreedToTerms: true })); if (isValid && form.agreedToTerms && !saving && isHourAvailable(selectedHour)) handleConfirm(); }}>

            <div className="confirm-terms">
              <label className="checkbox-label">
                <input type="checkbox" name="agreedToTerms" checked={!!form.agreedToTerms} onChange={handleInputChange} onBlur={handleBlur} />
                <span>Запознат съм с <a href="/terms" target="_blank" rel="noopener noreferrer">условията за ползване.</a></span>
              </label>
              {touched.agreedToTerms && !form.agreedToTerms && <div className="field-error">Трябва да се съгласите с условията, за да продължите.</div>}
            </div>

            <button className="confirm-btn-modern" type="submit" disabled={!isValid || !form.agreedToTerms || saving || !isHourAvailable(selectedHour)}>
              {saving ? 'Записване...' : 'Потвърждаване'}
            </button>
          </form>
        )}

        {confirmed && (
          <div className="modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal-card">
              <h3>Успех</h3>
              <p>Успешно запазихте час за {formatDateDisplay(selectedDate)} в {selectedHour}.</p>
              <div style={{textAlign: 'right'}}>
                <button type="button" className="confirm-btn-modern" onClick={closeModal}>Затвори</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingPage;
